/**
 * <mqc-iphone-viewer> — real GLTF model viewer (Three.js + GLTFLoader)
 * for the iPhone 17 Pro model, replacing the CSS-drawn phone in the hero
 * when `data-model-src` is present and WebGL is supported.
 *
 * The model (iphone17-optimized.glb) is a single-file, Meshopt-compressed
 * glTF with WebP textures — originally a 10.1MB multi-file glTF (.gltf +
 * .bin + 8 PNG/JPEG textures = 10 requests), re-encoded with gltf-transform
 * (dedup + prune + WebP q90 + EXT_meshopt_compression) down to one 3.1MB
 * .glb (1 request), same visual quality. Decoding Meshopt-compressed
 * geometry requires this MeshoptDecoder — without it, loader.loadAsync()
 * throws and the element falls back to the CSS-drawn phone.
 *
 * Three.js itself is NOT vendored into this theme (it's a large library
 * with no npm/bundler step available in Shopify's asset pipeline) — it's
 * imported from a version-pinned CDN as native ES modules. The three.js
 * "examples/jsm" addons (GLTFLoader, OrbitControls, RoomEnvironment)
 * import the core build using the bare specifier `from 'three'`, which
 * only resolves because of the import map rendered in <head> via
 * snippets/three-importmap.liquid — without it, every import below
 * fails silently and this element falls back to the CSS-drawn phone.
 *
 * Design goals (mirroring the project's existing performance discipline):
 *  - Lazy: the model + Three.js itself only load once the viewer scrolls
 *    near the viewport (IntersectionObserver), never blocking first paint.
 *  - Render-on-demand: the render loop stays idle unless the camera is
 *    moving (drag/damping/auto-rotate) or the canvas resized — no wasted
 *    GPU cycles while the model is just sitting still on screen.
 *  - Graceful fallback: if WebGL isn't available, or the model fails to
 *    load, the element quietly hides itself so the section's existing
 *    CSS-drawn phone (rendered behind it, not removed) shows instead.
 */


class MqcIphoneViewer extends HTMLElement {
  connectedCallback() {
    this.modelSrc = this.getAttribute('data-model-src');
    if (!this.modelSrc) {
      this.#fallback('no se configuró data-model-src');
      return;
    }
    if (!this.#supportsWebGL()) {
      this.#fallback('el navegador no soporta WebGL');
      return;
    }

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoRotate = this.getAttribute('data-autorotate') !== 'false' && !this.reduced;
    this.scaleMultiplier = parseFloat(this.getAttribute('data-scale') || '1');
    this.rotationOffset = {
      x: parseFloat(this.getAttribute('data-rotate-x') || '0'),
      y: parseFloat(this.getAttribute('data-rotate-y') || '0'),
      z: parseFloat(this.getAttribute('data-rotate-z') || '0'),
    };

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'mqc-iphone-viewer__canvas';
    this.appendChild(this.canvas);

    this.loaded = false;
    this.needsRender = true;
    this.disposed = false;

    this.io = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        this.isInView = isVisible;
        if (isVisible && !this.loaded) {
          this.loaded = true;
          this.#init().catch((err) => {
            this.#fallback(err);
          });
        }
        this.needsRender = true;
      },
      { threshold: 0.05 }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.disposed = true;
    this.io?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.controls?.dispose();
    this.renderer?.dispose();
  }

  #supportsWebGL() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
    } catch {
      return false;
    }
  }

  #fallback(reason) {
    this.classList.add('is-unavailable');
    if (reason) console.error('[mqc-iphone-viewer] fallback:', reason);
    // Only ever show the on-page error text inside the theme editor preview
    // (request.design_mode) — real visitors just see the CSS phone, never a
    // red error banner, even if something fails on the live site.
    if (reason && this.getAttribute('data-debug') === 'true') {
      const message = reason instanceof Error ? reason.message : String(reason);
      const debug = document.createElement('div');
      debug.textContent = `Modelo 3D no disponible: ${message}`;
      debug.setAttribute(
        'style',
        'position:absolute;left:8px;bottom:8px;right:8px;z-index:5;' +
          'font:11px/1.4 monospace;color:#b00020;background:#fff;border:1px solid #b00020;' +
          'border-radius:6px;padding:6px 8px;pointer-events:none;'
      );
      this.appendChild(debug);
    }
    this.dispatchEvent(new CustomEvent('mqc:viewer-fallback', { bubbles: true, detail: { reason } }));
  }

  async #init() {
    const [THREE, { GLTFLoader }, { OrbitControls }, { RoomEnvironment }, { MeshoptDecoder }] = await Promise.all([
      import('three'),
      import('three/addons/loaders/GLTFLoader.js'),
      import('three/addons/controls/OrbitControls.js'),
      import('three/addons/environments/RoomEnvironment.js'),
      import('three/addons/libs/meshopt_decoder.module.js'),
    ]);

    if (this.disposed) return;
    this.THREE = THREE;

    const scene = new THREE.Scene();
    scene.background = null; // transparent — blends with the section's own background

    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 6);

    const renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    // ---- realistic 3-point lighting + a synthetic studio environment for
    // premium reflections (no external HDR file was provided with this model) ----
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
    keyLight.position.set(3, 4, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    keyLight.shadow.radius = 6;
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xeaf2ff, 0.8);
    fillLight.position.set(-4, 1, 2);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 1.1);
    rimLight.position.set(0, 2, -5);
    scene.add(rimLight);

    scene.add(new THREE.AmbientLight(0xffffff, 0.35));

    // soft contact shadow “pedestal” so the phone doesn't float without grounding
    const shadowGround = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 20),
      new THREE.ShadowMaterial({ opacity: 0.18 })
    );
    shadowGround.rotation.x = -Math.PI / 2;
    shadowGround.position.y = -1.6;
    shadowGround.receiveShadow = true;
    scene.add(shadowGround);

    // ---- controls: damped drag-to-rotate, no pan, limited zoom ----
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 3.2;
    controls.maxDistance = 9;
    controls.minPolarAngle = Math.PI / 4;
    controls.maxPolarAngle = Math.PI - Math.PI / 4;
    controls.autoRotate = this.autoRotate;
    controls.autoRotateSpeed = 0.6;
    controls.addEventListener('change', () => { this.needsRender = true; });
    controls.addEventListener('start', () => { controls.autoRotate = false; });

    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.controls = controls;

    // ---- load the model ----
    const stage = this.parentElement;
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.loadAsync(this.modelSrc, (event) => {
      if (this.disposed || !stage) return;
      const pct = event.total ? Math.min(event.loaded / event.total, 0.95) : 0;
      stage.style.setProperty('--mldr-p', pct.toFixed(3));
      stage.classList.toggle('mldr-p1', pct >= 0.30);
      stage.classList.toggle('mldr-p2', pct >= 0.60);
      stage.classList.toggle('mldr-p3', pct >= 0.90);
    });
    if (this.disposed) return;

    const model = gltf.scene;
    model.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = false;
        if (node.material) node.material.envMapIntensity = 1.1;
      }
    });

    // center via bounding box, then auto-scale to a consistent on-screen size
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    model.position.sub(center); // center at origin
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const targetSize = 2.5; // consistent visual size regardless of source scale — leaves headroom so the phone isn't cropped top/bottom
    const autoScale = (targetSize / maxDim) * this.scaleMultiplier;
    model.scale.setScalar(autoScale);

    // orientation correction — exposed via data attributes since "correct"
    // orientation can't be detected automatically without a reference;
    // defaults to identity (no correction) unless the section settings
    // specify an offset.
    model.rotation.set(
      THREE.MathUtils.degToRad(this.rotationOffset.x),
      THREE.MathUtils.degToRad(this.rotationOffset.y),
      THREE.MathUtils.degToRad(this.rotationOffset.z)
    );

    scene.add(model);
    this.model = model;

    // ---- animations, if the model has any (AnimationMixer) ----
    if (gltf.animations && gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(model);
      gltf.animations.forEach((clip) => this.mixer.clipAction(clip).play());
    }

    // Drive loader overlay to completion before the crossfade starts
    if (stage) {
      stage.style.setProperty('--mldr-p', '1');
      stage.classList.add('mldr-p1', 'mldr-p2', 'mldr-p3');
    }
    this.classList.add('is-loaded');
    // The real model is confirmed working now — actively hide the CSS-drawn
    // phone behind it instead of just relying on z-index/transparency, since
    // the canvas has a transparent background and the model doesn't cover
    // every pixel of it, which let the old phone show through at the edges.
    const cssPhone = stage?.querySelector('.mqc-hero-render__rig-wrap');
    if (cssPhone) cssPhone.style.display = 'none';
    this.dispatchEvent(new CustomEvent('mqc:viewer-loaded', { bubbles: true }));

    this.#observeResize();
    this.clock = new THREE.Clock();
    this.needsRender = true;
    this.rafId = requestAnimationFrame(this.#tick.bind(this));
  }

  #observeResize() {
    this.resizeObserver = new ResizeObserver(() => {
      const { width, height } = this.getBoundingClientRect();
      if (!width || !height) return;
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.needsRender = true;
    });
    this.resizeObserver.observe(this);
  }

  #tick() {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.#tick.bind(this));

    if (!this.isInView) return; // paused while scrolled away — saves battery/CPU

    const delta = this.clock.getDelta();
    const controlsMoved = this.controls.update(); // true while damping/auto-rotate is still settling
    if (this.mixer) this.mixer.update(delta);

    if (this.needsRender || controlsMoved || this.controls.autoRotate) {
      this.renderer.render(this.scene, this.camera);
      this.needsRender = false;
    }
  }
}

if (!customElements.get('mqc-iphone-viewer')) {
  customElements.define('mqc-iphone-viewer', MqcIphoneViewer);
}
